import { useRef, useState } from "react";
import styles from "../list.module.css";
import { Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, Checkbox } from "@mui/material";
import { ChevronDown, ChevronUp, Edit3, Map, Plus, X } from "react-feather";
import { FileDownloadOutlined, PersonAddAltOutlined, PrintOutlined } from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";
import TablePaginationComponent from "../../../../components/TablePagination";
import { StyledMainTableCell, StyledMainTableRow } from "../../../../components/TableComponent";
import { authData } from "../../../../components/GetAuth";
import { ComponentToPrintList } from "../ComponentToPrintList";
import BookingItemsPanel, { eligibleItemIds } from "../BookingItemsPanel";
import ListToolbar from "../../../../components/ListToolbar/ListToolbar";
import { downloadCsv } from "../../../../components/ListToolbar/csv";
import { DELIVERY_EXPORT_COLUMNS } from "../ListView";

// Map view, step 1: tick items, choose drivers, then plan the routes
const Step1 = ({ selected, driverList, selectedDriverList, filteredData, filterVars, setFilterVars, setFormOpen, setSelectedDriverList, getOptimizeRoute, toggleItem, toggleBookingSelection, setSelected, setPaymentModalOpen, setSelectBooking, setNoteModalOpen, setNoteData, viewSwitch }) => {
    const printRef = useRef();
    const handlePrint = useReactToPrint({ content: () => printRef.current });
    const auth = authData();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [expandedId, setExpandedId] = useState(null);

    const canAssign = auth?.user_type === 2 || auth?.permissions?.indexOf('delivery:assign-driver') > -1;

    const paginationChangePage = (event, newPage) => {
        setPage(newPage);
    }

    const paginationChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }

    // Every eligible item across the whole (filtered) list — drives the
    // header "select all" checkbox.
    const allEligibleIds = filteredData?.flatMap((b) => eligibleItemIds(b)) || [];
    const allSelected = allEligibleIds.length > 0 && allEligibleIds.every((id) => selected.indexOf(id) > -1);
    const someSelected = allEligibleIds.some((id) => selected.indexOf(id) > -1);

    return (<>
        {selectedDriverList?.length > 0 && <div>
            <p className={`${styles.DriverTitle}`}>Selected driver{selectedDriverList?.length > 1 ? "s" : ""} <span className={styles.Mono}>({selectedDriverList?.length})</span></p>
            <div className={`${styles.DriverCardSec}`}>
                {driverList?.map(item => {
                    return (selectedDriverList?.indexOf(item?.id) > -1) ? <div className={`${styles.DriverCard}`} key={item?.id}>
                        <div className={`${styles.DriverCardUnder}`}>
                            <div className={`${styles.DriverCardHead}`}>
                                <p>{item?.name}</p>
                                <button aria-label={`Remove ${item?.name}`} onClick={(e) => {
                                    setSelectedDriverList((prev) => {
                                        return prev?.filter((i) => i !== item?.id);
                                    });
                                }}>
                                    <X size={16} />
                                </button>
                            </div>
                            <p className={`${styles.DriverCardAddress}`}>{item?.address}</p>
                        </div>
                    </div> : null;
                })}
            </div>
        </div>}

        <ListToolbar
            search={filterVars?.searchKey}
            onSearch={(value) => setFilterVars((prev) => ({ ...prev, searchKey: value }))}
            dateRange={filterVars?.date}
            onDateRange={(value) => setFilterVars((prev) => ({ ...prev, date: value }))}
            viewSwitch={viewSwitch}
            selectedCount={selected.length}
            onClearSelection={() => setSelected([])}
            secondaryAction={canAssign ? { label: selectedDriverList?.length ? `Choose drivers (${selectedDriverList.length})` : "Choose drivers", icon: <PersonAddAltOutlined />, onClick: () => setFormOpen(true) } : null}
            primaryAction={canAssign ? {
                label: selected.length ? `Plan route (${selected.length})` : "Plan route",
                icon: <Map size={16} />,
                onClick: () => getOptimizeRoute(),
                disabled: selected.length === 0 || !selectedDriverList?.length,
                hint: selected.length === 0 ? "Tick the items to route first" : "Choose at least one driver first",
            } : null}
            menuItems={[
                { label: "Export CSV", icon: <FileDownloadOutlined fontSize="small" />, onClick: () => downloadCsv("delivery-items.csv", DELIVERY_EXPORT_COLUMNS, filteredData), disabled: !filteredData?.length },
                { label: "Print bookings", icon: <PrintOutlined fontSize="small" />, onClick: handlePrint, disabled: !filteredData?.length },
            ]}
        />
        <div style={{ display: "none" }}>
            <ComponentToPrintList ref={printRef} data={filteredData} />
        </div>

        <div className={`${styles.CusTable}`}>
            <TableContainer component={Paper} className={styles.ResponsiveTableWrap}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            {canAssign && <StyledMainTableCell padding="checkbox">
                                <Checkbox
                                    className={`${styles.AllCheckbox}`}
                                    color="primary"
                                    indeterminate={someSelected && !allSelected}
                                    checked={allSelected}
                                    disabled={allEligibleIds.length === 0}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelected((prev) => Array.from(new Set([...prev, ...allEligibleIds])));
                                            return;
                                        }
                                        setSelected((prev) => prev.filter((id) => allEligibleIds.indexOf(id) === -1));
                                    }}
                                />
                            </StyledMainTableCell>}
                            <StyledMainTableCell>Booking Id</StyledMainTableCell>
                            <StyledMainTableCell>Customer Name</StyledMainTableCell>
                            <StyledMainTableCell>Recepient Name</StyledMainTableCell>
                            <StyledMainTableCell>Delivery Address</StyledMainTableCell>
                            <StyledMainTableCell>Delivery Notes</StyledMainTableCell>
                            <StyledMainTableCell>Payment Status</StyledMainTableCell>
                            <StyledMainTableCell>Items</StyledMainTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => {
                            const eligibleIds = eligibleItemIds(item);
                            const bookingAllSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selected.indexOf(id) > -1);
                            const bookingSomeSelected = eligibleIds.some((id) => selected.indexOf(id) > -1);
                            const isExpanded = expandedId === item.id;
                            const totalItems = item?.products?.length || 0;

                            return (<>
                                <StyledMainTableRow key={item?.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                    {canAssign && <TableCell padding="checkbox">
                                        <Checkbox
                                            color="primary"
                                            indeterminate={bookingSomeSelected && !bookingAllSelected}
                                            checked={bookingAllSelected}
                                            disabled={eligibleIds.length === 0}
                                            onChange={() => toggleBookingSelection(item)}
                                        />
                                    </TableCell>}
                                    <TableCell component="th" scope="row" data-label="Booking Id" className={styles.Mono}>{item?.booking_id}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Customer Name">{item?.customer_name}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Recepient Name">{item?.recipient_details?.name}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Delivery Address">{item?.recipient_details?.address}</TableCell>
                                    <TableCell component="th" scope="row" data-label="Delivery Notes">{item?.delivery_note ? <><p>{item?.delivery_note}</p><button className={`${styles.AddNote}`} onClick={(e) => { e.preventDefault(); setNoteModalOpen(true); setNoteData(item?.delivery_note); setSelectBooking(item); }}><Edit3 size={12} /> Update</button></> : <button className={`${styles.AddNote}`} onClick={(e) => { e.preventDefault(); setNoteModalOpen(true); setNoteData(null); setSelectBooking(item); }}><Plus /> Add Note</button>}</TableCell>
                                    <TableCell data-label="Payment Status">
                                        {item?.payment_status === 1 || item?.payment_status === "1" ? (<Button className={`${styles.StatusBU} ${styles.DisaBU}`}>Paid</Button>) : (<Button className={`${styles.StatusBU}`} onClick={(e) => { setPaymentModalOpen(true); setSelectBooking(item); }}>Unpaid</Button>)}
                                    </TableCell>
                                    <TableCell data-label="Items">
                                        <button className={styles.ExpandToggle} onClick={(e) => setExpandedId(isExpanded ? null : item.id)}>
                                            <span className={styles.ItemCountBadge}>{eligibleIds.length}/{totalItems} ready</span>
                                            {isExpanded ? <ChevronUp /> : <ChevronDown />}
                                        </button>
                                    </TableCell>
                                </StyledMainTableRow>

                                {isExpanded && <TableRow className={styles.ItemPanelRow}>
                                    <TableCell colSpan={canAssign ? 8 : 7}>
                                        <BookingItemsPanel booking={item} selected={selected} onToggleItem={toggleItem} />
                                    </TableCell>
                                </TableRow>}
                            </>);
                        })}

                        {filteredData.length === 0 && (<TableRow>
                            <TableCell colSpan={10} align="center">No data found</TableCell>
                        </TableRow>)}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredData?.length > 0 && (<div className={styles.PaginationWrap}>
                <TablePaginationComponent
                    count={filteredData?.length}
                    colSpan={6}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleChangePage={paginationChangePage}
                    handleChangeRowsPerPage={paginationChangeRowsPerPage}
                />
            </div>)}
        </div>
    </>);
};

export default Step1;